import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    AfterViewInit,
    ViewChildren,
    ElementRef,
    QueryList,
    HostListener
} from '@angular/core';

export interface MenuEntry {
    section: string,
    id: string;
    name: string;
    children?: MenuEntry[];
}

@Component({
    selector: 'drilldown-menu',
    template: `
    <div class="drilldown-container" tabindex="0">
      <div class="breadcrumb">
        <a (click)="navigateHome($event)" href [class.disabled]="path?.length === 0">{{rootName}}</a>
        <ng-container *ngFor="let crumb of breadcrumbs">
          <span *ngIf="crumb.index >= 0" style="margin: 0 .25em;">/</span>
          <a *ngIf="crumb.index < (breadcrumbs.length - 1)" href (click)="navigateBackTo($event, crumb.index)">
            {{ crumb.name }}
          </a>
          <span *ngIf="crumb.index === (breadcrumbs.length - 1)">
            {{ crumb.name }}
          </span>
          <span *ngIf="crumb.index === -1" style="margin: 0 .25em;">/ ...</span>
        </ng-container>
      </div>

      <ul class="drilldown-menu"
          [ngClass]="{
            'slide-in-right': animationDirection === 'forward',
            'slide-in-left': animationDirection === 'backward'
          }"
          (animationend)="animationDirection = null">
        <li #menuItemElem *ngFor="let item of currentMenu; let i = index"
            [attr.tabindex]="i === focusedIndex ? 0 : -1"
            (click)="navigateTo($event, item)"
            [class.active]="i === focusedIndex">
          <a href="javascript:void(0)">
            {{ item.name }}
            <span *ngIf="item.children?.length" class="glyphicon glyphicon-menu-right pull-right"></span>
            <span *ngIf="isItemSelected(item)" class="glyphicon glyphicon-ok pull-right"></span>
          </a>
        </li>
      </ul>
    </div>
  `,
    styles: [`
    .drilldown-container {
      outline: none;
      font-weight: normal;
    }

    .breadcrumb {
      padding: 10px 12px;
      margin: 0;
      white-space: nowrap;
      overflow-x: auto;
    }

    .breadcrumb-link {
      cursor: pointer;
      color: #337ab7;
      text-decoration: none;
    }

    .breadcrumb-link:hover {
      text-decoration: underline;
    }

    .drilldown-menu {
      display: block;
      max-height: 250px;
      overflow-y: auto;
      margin: 0;
      padding: 3px 0 0 0;
      border-top: 1px solid #ddd;
    }

    .drilldown-menu > li:hover {
      background-color: #f5f5f5;
      cursor: pointer;
    }

    .drilldown-menu > li > a {
      display: block;
      padding: 8px 12px;
      clear: both;
      color: #333;
      white-space: nowrap;
      text-decoration: none;
    }

    .glyphicon-ok {
      color: #5cb85c;
    }

    @keyframes slideInRight {
      from { transform: translateX(100%); opacity: 0; }
      to   { transform: translateX(0); opacity: 1; }
    }

    @keyframes slideInLeft {
      from { transform: translateX(-100%); opacity: 0; }
      to   { transform: translateX(0); opacity: 1; }
    }

    .slide-in-right {
      animation: slideInRight 0.3s ease-out;
    }

    .slide-in-left {
      animation: slideInLeft 0.3s ease-out;
    }
  `]
})
export class DrillDownMenuComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() menu: MenuEntry[] = [];
    @Input() rootName: string = "Root";
    @Input() selectedItem: {section?: string, id: string} = { section: null, id: null };
    @Output() entrySelected = new EventEmitter<MenuEntry>();
    @Output() menuClosed = new EventEmitter<void>();

    path: MenuEntry[] = [];
    focusedIndex = 0;
    initialSelectedItem: any;
    animationDirection: 'forward' | 'backward' | null = null;
    currentVisibleMenu: MenuEntry[] = [];

    @ViewChildren('menuItemElem') menuItems: QueryList<ElementRef>;

    get currentMenu(): MenuEntry[] {
        return this.currentVisibleMenu;
    }

    get breadcrumbs(): { name: string, index: number }[] {
        const len = this.path.length;
        if (len <= 2) {
            return this.path.map((e, i) => ({ name: e.name, index: i }));
        }
        return [
            { name: this.path[0].name, index: 0 },
            { name: '...', index: -1 },
            { name: this.path[len - 1].name, index: len - 1 }
        ];
    }

    ngOnInit() {
        this.initialSelectedItem = this.selectedItem;
        this.setupPathToSelected();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedItem && !changes.selectedItem.firstChange) {
            this.initialSelectedItem = this.selectedItem;
            this.setupPathToSelected();
        }
    }

    ngAfterViewInit() {
        setTimeout(() => this.ensureVisible(), 0);
    }

    setupPathToSelected(): void {
        this.path = [];
        if (!this.selectedItem || !this.menu) {
            this.currentVisibleMenu = this.menu;
            return;
        }

        const fullPath = this.findPathTo(this.menu, this.selectedItem);
        if (fullPath.length > 0) {
            const selectedNode = fullPath[fullPath.length - 1];

            // Special case: if selected node has children, treat it like a folder and navigate into it
            if (selectedNode.children && selectedNode.children.length > 0) {
                this.path = fullPath; // include the selected node itself into path
                this.currentVisibleMenu = selectedNode.children;
                this.focusedIndex = 0;
            } else {
                // Standard case: selected node is a leaf
                this.path = fullPath.slice(0, -1);
                this.currentVisibleMenu = this.path.length
                    ? this.path[this.path.length - 1].children || []
                    : this.menu;
                this.focusedIndex = this.currentVisibleMenu.findIndex(item => (item.section === this.selectedItem?.section && item.id === this.selectedItem?.id));
                if (this.focusedIndex === -1) {
                    this.focusedIndex = 0;
                }
            }
        } else {
            this.currentVisibleMenu = this.menu;
            this.focusedIndex = 0;
        }

        setTimeout(() => this.ensureVisible(), 0);
    }

    navigateTo(event: any, entry: MenuEntry): void {
        if (entry.children?.length) {
            this.stopClosure(event);
            this.animationDirection = 'forward';
            setTimeout(() => {
                this.path.push(entry);
                this.currentVisibleMenu = entry.children!;
                this.focusedIndex = 0;
                setTimeout(() => this.ensureVisible(), 0);
            }, 10);
        } else {
            this.selectedItem = entry;
            this.entrySelected.emit(entry);
            this.menuClosed.emit();
        }
    }

    navigateBackTo(event: any, index: number): void {
        this.stopClosure(event);
        this.animationDirection = 'backward';
        setTimeout(() => {
            this.path = this.path.slice(0, index + 1);
            this.currentVisibleMenu = this.path.length
                ? this.path[this.path.length - 1].children || []
                : this.menu;
            this.focusedIndex = 0;
            setTimeout(() => this.ensureVisible(), 0);
        }, 10);
    }

    navigateHome(event: any): void {
        this.stopClosure(event);
        this.animationDirection = 'backward';
        setTimeout(() => {
            this.path = [];
            this.currentVisibleMenu = this.menu;
            this.focusedIndex = 0;
            setTimeout(() => this.ensureVisible(), 0);
        }, 10);
    }

    private stopClosure(event: any): void {
        if (!event) return;
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
    }

    private findPathTo(nodes: MenuEntry[], targetItem: { section?: string, id?: string}, trail: MenuEntry[] = []): MenuEntry[] {
        for (const node of nodes) {
            const newTrail = [...trail, node];
            if (node.section == targetItem.section && node.id === targetItem.id) return newTrail;
            if (node.children) {
                const result = this.findPathTo(node.children, targetItem, newTrail);
                if (result.length) return result;
            }
        }
        return [];
    }

    private ensureVisible(): void {
        const itemElems = this.menuItems?.toArray();
        const el = itemElems?.[this.focusedIndex];

        if (el) {
            el.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

            // Force focus to the actual list item
            el.nativeElement.focus();
        } else if (itemElems?.length) {
            // fallback if focusedIndex invalid
            itemElems[0].nativeElement.focus();
        }
    }

    isItemSelected(item: MenuEntry): boolean {
        if (item.children?.length) {
            return false;
        }
        return item.section === this.selectedItem?.section &&
            item.id === this.selectedItem?.id;
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        const max = this.currentMenu.length - 1;
        const focusedItem = this.currentMenu[this.focusedIndex];

        switch (event.key) {
            case 'ArrowDown':
                this.focusedIndex = Math.min(this.focusedIndex + 1, max);
                break;
            case 'ArrowUp':
                this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
                break;
            case 'ArrowRight':
                if (focusedItem?.children?.length) {
                    this.navigateTo(event, focusedItem);
                }
                break;
            case 'ArrowLeft':
                if (this.path.length > 0) {
                    this.navigateBackTo(event, this.path.length - 2);
                }
                break;
            case 'Enter':
                if (!focusedItem?.children?.length) {
                    this.selectedItem = focusedItem;
                    this.entrySelected.emit(focusedItem);
                    this.menuClosed.emit();
                } else {
                    this.navigateTo(event, focusedItem);
                }
                break;
            case 'Escape':
                this.selectedItem = this.initialSelectedItem;
                this.menuClosed.emit();
                break;
        }

        setTimeout(() => this.ensureVisible(), 0);
        event.preventDefault();
    }
}
